import { useRef, useState } from 'react';

import { Button, Heading, cn } from '@simplywallst/ui-core/next';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import WarningSolid from '@simplywallst/ui-core/icons/WarningSolid';
import SearchSolid from '@simplywallst/ui-core/icons/SearchSolid';
import AiSolid from '@simplywallst/ui-core/icons/AiSolid';
import UploadSolid from '@simplywallst/ui-core/icons/UploadSolid';

import Loader from '@assets/images/portfolio/loader.svg';
import { useTracking } from 'react-tracking';

type Props = {
  onImport: (file: File) => void;
  onClose: () => void;
  uploadStatus: 'idle' | 'pending' | 'success' | 'error';
  selectedFileName: string;
  populatedRowCount: number;
};

export const DefaultState = ({
  onImport,
  onClose,
  uploadStatus,
  selectedFileName,
  populatedRowCount,
}: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'uploadTransactionsModal',
  });

  const isValidFileType = (file: File): boolean => {
    if (file.type !== 'text/csv') {
      setErrorMsg(
        `${file.name} isn't a .csv - Please try again with a valid .csv`
      );
      return false;
    }
    return true;
  };

  const handleDropFile = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      if (isValidFileType(droppedFile)) {
        onImport(droppedFile);
      }
    }
    trackEvent({
      modifier: 'dropFile',
      action: 'dropped',
    });
  };

  const handleBrowseFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const field = inputRef.current;
    field && field.click();
    trackEvent({
      modifier: 'browseFile',
      action: 'click',
    });
  };

  const fileName = file?.name || selectedFileName;

  return (
    <div className="relative grid gap-x1 rounded-md bg-p-white px-6 py-x4">
      <Button
        variant="subtle"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-2 p-0"
      >
        <CrossSolid fill="spacegray" />
      </Button>
      <Heading
        as="h2"
        size="xLarge"
        className="flex gap-x1 text-pretty text-p-grey-100"
      >
        Import transaction instantly via CSV
        <AiSolid
          className={cn(
            'size-7',
            uploadStatus === 'pending' && 'animate-pulse'
          )}
        />
      </Heading>
      <p className="m-0 text-sm text-soft">
        Upload your transaction file and AI will organize it for you
      </p>

      <div className="my-x1 rounded-md bg-surface-2 p-x2">
        <div className="flex items-center gap-x1">
          <SearchSolid className="fill-warning" />
          <div>
            <p className="m-0 text-xs font-medium text-solid">
              Need help finding and uploading your transactions?
            </p>
            <p className="m-0 text-xs text-soft">
              Learn{' '}
              <a
                className="text-soft"
                href="https://support.simplywall.st/hc/en-us/articles/10974593931151-How-to-Bulk-Add-Transactions-to-Create-Your-Stock-Portfolio"
                target="_blank"
                rel="noreferrer"
              >
                how to download your transactions history
              </a>{' '}
              from your broker
            </p>
          </div>
        </div>
      </div>

      <div
        className="grid h-[234px] items-center justify-center rounded-xl border-2 border-dashed border-p-grey-30/25 bg-p-white"
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={handleDropFile}
      >
        {populatedRowCount > 0 && (
          <div className="mx-x2 flex items-center rounded-md bg-surface-2 p-x2 text-xs text-solid md:mx-0">
            <WarningSolid className="mr-2 fill-warning" /> Uploading a file will
            overwrite all your existing transactions.
          </div>
        )}
        <input
          ref={inputRef}
          aria-label="Upload file"
          type="file"
          accept=".csv"
          className="hidden w-full"
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              const file = e.target.files[0];
              setFile(file);
              if (isValidFileType(file)) {
                onImport(file);
              }
            }
          }}
        />
        <div className="grid justify-items-center gap-x2 text-soft">
          {uploadStatus === 'pending' ? (
            <Loader className="size-x4 animate-spin" />
          ) : (
            <div className="grid justify-items-center gap-x1">
              <UploadSolid className="fill-softer" />
              <p className="m-0 text-xs text-soft">
                Drag a CSV file here or
              </p>{' '}
              <Button
                type="button"
                size="large"
                variant="primary"
                onClick={handleBrowseFile}
                className="block"
              >
                Browse files
              </Button>
            </div>
          )}
          {errorMsg !== '' && uploadStatus !== 'pending' ? (
            <div className="flex h-12 items-center rounded-md bg-surface-3 px-2">
              <WarningSolid className="mr-2 fill-bad" />
              <div className="text-xs text-soft">{errorMsg}</div>
            </div>
          ) : (
            <div className=" text-sm text-soft">{fileName}</div>
          )}
        </div>
      </div>
      <div className="mt-x3 flex items-center justify-end gap-x2">
        <p className="m-0 text-xs text-soft">
          Prefer to add transactions manually?
        </p>
        <Button variant="outline" size="large" onClick={onClose}>
          Use table instead
        </Button>
      </div>
    </div>
  );
};
